import { useEffect, type ReactNode } from 'react';
import { FolderTabs, type FolderTab } from '../FolderTabs';

/** Logged once per session, not per render — one message is the point, not a stream. */
let warnedAboutInfoTab = false;

/**
 * The **header** profile layout — header strip on top, content below.
 *
 * The second of the product's two canonical profile shapes (`<ProfileShell>` is the first,
 * for a record whose content splits into panels a reader picks between). Use this one when
 * a record reads as one continuous thing: an audit entry, a role, a document.
 *
 * Four regions, top to bottom:
 *
 * 1. **Header** — `<ProfileHeader>`: the record's identity, state, and provenance.
 * 2. **Toolbar** *(optional)* — record-level `<ShortcutButton>` chips. Only for actions
 *    belonging to the whole record and appearing nowhere else; a field-level edit belongs to
 *    its `<ProfileCard>`, and a lifecycle action belongs to the banner.
 * 3. **Banner** *(optional in the type, expected in practice)* — `<ProfileBanner>`: what the
 *    record is right now. Rendered for every state, not only the bad ones.
 * 4. **Content** — folder tabs over one scrolling panel. The first tab is always `info`.
 *
 * The page itself never scrolls: everything above the content is fixed height and the panel
 * owns its own overflow, so the header stays put while a long tab scrolls under it.
 *
 * DELIBERATE DEVIATION from reference §7, recorded in CLAUDE.md — §7 describes a single
 * stacked layout; the product uses these two shapes instead.
 */
export function ProfileHeaderShell<K extends string>({
  header,
  toolbar,
  banner,
  tabs,
  activeTab,
  onTabChange,
  panel = 'cards',
  children,
  id,
}: {
  header: ReactNode;
  /**
   * Record-level action chips — `<ShortcutButton size="xs">`, which is the size this strip
   * is built around. Omit the prop entirely rather than rendering an empty strip.
   */
  toolbar?: ReactNode;
  /** Record-level state, above the tabs so it is true of whichever tab is open. */
  banner?: ReactNode;
  /**
   * The folder navs. The shell owns the strip — its inset, its ground, and the way the
   * active tab merges into the panel — so no page re-derives it. A tab the caller lacks
   * permission for is hidden by `<FolderTabs>`, which also re-selects the first tab the
   * caller can see when the active one is gated away.
   */
  /**
   * The record's regions, as folder tabs. **Omit all three for a record that has one.**
   *
   * A strip that picks between one thing is the dead affordance the hide rule forbids, so a
   * page passing no tabs gets no strip and a panel rounded on all four corners. The header,
   * the banner, the inset and the scroll region are what it came for, and it gets them from
   * the shell rather than hand-rolling a page — which is exactly what the department PPA page
   * did, and why it read as a table slammed against the window.
   *
   * It is all three or none: an active key with nothing to draw, or tabs nothing can move,
   * are each half a control.
   */
  tabs?: FolderTab<K>[];
  activeTab?: K;
  onTabChange?: (key: K) => void;
  /**
   * `cards` — a stack of `<ProfileCard>`s, padded and capped to a readable measure, exactly
   * as in the sidebar layout: a label/value table run out to the full viewport puts its
   * value a screen away from its label.
   *
   * `bleed` — the panel edge to edge, for content that owns its own chrome: a table with a
   * search strip and a pager.
   */
  panel?: 'cards' | 'bleed';
  children: ReactNode;
  /** Required: the tab strip is interactive, and every interactive element needs an id. */
  id: string;
}) {
  /*
    Every profile's first tab is `Info` — the record's basic information and its audit trail
    (CLAUDE.md). Caught in development rather than typed away: the constraint is about the
    ORDER a page declares its tabs in, which no signature can express.
  */
  useEffect(() => {
    if (!import.meta.env.DEV || warnedAboutInfoTab || !tabs || tabs.length === 0 || tabs[0].key === 'info') {
      return;
    }

    warnedAboutInfoTab = true;
    console.error(
      `<ProfileHeaderShell id="${id}"> declares "${tabs[0].key}" as its first tab — a ` +
        "profile's first tab is always `info`, holding the record's basic information and " +
        'its audit trail. See CLAUDE.md, "Profile pages — two layouts".',
    );
  }, [tabs, id]);

  return (
    <div id={id} className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      {header}

      {toolbar ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2">
          {toolbar}
        </div>
      ) : null}

      {/*
        The banner stands on the SAME grey as the tab strip below it, and draws no rule under
        itself. The two are one band of chrome between the header and the panel; a white strip
        with a border under it read as a third region, and the border landed a few pixels above
        the identical-looking one the strip already draws.
      */}
      {banner ? <div className="shrink-0 bg-slate-100 px-4 py-3">{banner}</div> : null}

      {/*
        The folder nav and the panel it merges into are ONE region, inset from the page on
        every side by the grey they stand on. Run edge to edge and the panel stops reading as
        a panel — it becomes the window, and the tabs look stuck to the browser chrome rather
        than attached to a surface.

        The margin belongs out here rather than on the panel: the tabs and the panel have to
        share the same left edge for the merge to line up, so one container pads both.
      */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-slate-100 px-4 pb-4">
        {/*
          The strip sits on a slate-100 ground — the darkest of the three surfaces, so the
          stack reads front to back: white active tab, slate-50 inactive tabs, slate-100
          behind them. The active tab carries a white bottom border over the panel's top
          border, which is what lifts it out of the ground and merges it into the panel. A
          white ground here would flatten all of that.
        */}
        {tabs && activeTab !== undefined && onTabChange ? (
          <div className="shrink-0 pt-2">
            <FolderTabs
              id={id}
              tabs={tabs}
              activeKey={activeTab}
              onChange={onTabChange}
              ariaLabel="Record sections"
              /*
                Indented past the panel's corner radius, so the first tab does not sit over the
                curve it is meant to merge into — the same reason the index status strip uses
                this value.
              */
              paddingX="px-3"
            />
          </div>
        ) : (
          // Nothing sits on the panel's top edge, so it needs the clearance the strip was
          // giving it — or the panel butts against the banner above, which is the touching
          // failure this product names.
          <div className="h-4 shrink-0" />
        )}

        {/*
          Per-panel scrolling, per reference §7 — the page body never scrolls.

          Bordered on all four sides and rounded at the bottom only: the top corners stay
          square because the tab strip sits on them, and the top border is the line the active
          tab breaks to merge with. It is `slate-300` — FolderTabs' own PANEL_BORDER — or the
          seam shows where the tab meets it.
        */}
        {/*
          `overflow-x-hidden` is NOT redundant beside `overflow-y-auto`, and leaving it off is
          what put a SECOND horizontal scrollbar under this panel. CSS computes a `visible` axis
          to `auto` the moment the other axis is not visible — so `overflow-y-auto` alone makes
          the panel scroll sideways too, and any child a pixel too wide silently grows a bar
          beneath the one the table already draws for itself.

          The contract this states is the one the shell always meant: the PANEL scrolls
          vertically, and a wide child owns its horizontal scroll — which is exactly what
          `panel="bleed"` is for and what `<DataTable>` does inside it. A `cards` panel is
          capped at a readable measure and cannot overflow at all.
        */}
        <div
          className={`modern-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden border border-slate-300 bg-white ${
            tabs ? 'rounded-b-panel' : 'rounded-panel'
          }`}
        >
          {panel === 'cards' ? (
            <div className="mx-auto flex min-w-0 max-w-4xl flex-col gap-5 p-5">{children}</div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}
